import {
  Component,
  forwardRef,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
  HostBinding,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';

/**
 * Like SearchableMultiSelectComponent, but works with arbitrary objects instead
 * of plain strings. Provide a `display` function to render each option; the full
 * object is preserved as the control value so callers can read an ID or other
 * data back off the selection directly.
 */
@Component({
  selector: 'app-searchable-object-multi-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
  ],
  templateUrl: './searchable-object-multi-select.component.html',
  styleUrls: ['./searchable-object-multi-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableObjectMultiSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableObjectMultiSelectComponent<T = any>
  implements OnInit, OnDestroy, ControlValueAccessor, OnChanges
{
  @Input() label: string = '';
  @Input() placeholder: string = 'Select';
  @Input() options: T[] = [];
  @Input() inputStyle: string = '';
  @Input() required: boolean = false;
  @Input() multiple: boolean = true;

  /** Renders the visible label for an option. */
  @Input({ required: true }) display!: (option: T) => string;
  /** Determines whether two options represent the same value. */
  @Input({ required: true }) compare!: (a: T, b: T) => boolean;
  /** Determines whether an option should be disabled. */
  @Input() disabled: (option: T) => boolean = () => false;

  @Output() selectionChange = new EventEmitter<T[] | T>();

  @ViewChild('search') searchTextBox!: ElementRef;
  @ViewChild(MatSelect) matSelect!: MatSelect;

  selectFormControl = new FormControl();
  searchTextboxControl = new FormControl();

  public filteredOptions?: Observable<T[]>;
  private destroy$ = new Subject<void>();

  onChange: (value: T[] | T) => void = () => {};
  onTouched: () => void = () => {};

  // compareWith is bound in the template; keep it as an arrow so `this` is preserved.
  compareWith = (a: T, b: T): boolean => (a == null || b == null ? a === b : this.compare(a, b));

  private setupFilteredOptions() {
    this.filteredOptions = this.searchTextboxControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value))
    );
  }

  ngOnInit() {
    this.setupFilteredOptions();

    // When the selection changes, propagate it to the parent form
    this.selectFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(value);
      this.selectionChange.emit(value);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setupFilteredOptions();
  }

  writeValue(value: T[] | T): void {
    this.selectFormControl.setValue(value ?? (this.multiple ? [] : null), { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // --- Component Logic ---
  private _filter(value: string): T[] {
    const filterValue = value ? value.toLowerCase() : '';
    const filtered = this.options.filter((o) => this.display(o).toLowerCase().includes(filterValue));

    const selectedValue = this.selectFormControl.value;
    const selected: T[] = Array.isArray(selectedValue) ? selectedValue : selectedValue != null ? [selectedValue] : [];

    // ensure selected options always remain in the list
    const result = [...selected];
    for (const option of filtered) {
      if (!result.some((s) => this.compare(s, option))) {
        result.push(option);
      }
    }
    return result;
  }

  onPanelOpen() {
    this.searchTextboxControl.setValue('');
    setTimeout(() => this.searchTextBox.nativeElement.focus(), 100);
  }

  onPanelClose() {
    this.onTouched();
  }

  clearSearch(event: MouseEvent) {
    event.stopPropagation();
    this.searchTextboxControl.setValue('');
  }

  @HostBinding('class.is-required')
  get isRequired() {
    return this.required;
  }
}
